import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

function formatError(detail) {
    if (!detail) return "Erro desconhecido. Tente novamente.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function Login() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    if (user && user.id) {
        return <Navigate to="/" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) {
            toast.error("Preencha email e senha");
            return;
        }
        setLoading(true);
        try {
            await login(email.trim(), password);
            toast.success("Bem-vindo!");
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || "Erro ao entrar");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary/5 via-background to-accent/10" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8 animate-fade-in-up">
                    <div className="mx-auto w-24 h-24 rounded-2xl bg-emerald-900 flex items-center justify-center p-2 shadow-xl border-2 border-emerald-800">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="mt-4 text-3xl font-extrabold text-primary">COOMFLONA</h1>
                    <p className="text-sm text-muted-foreground mt-1">Setor de Transporte</p>
                </div>

                <Card className="border-border shadow-lg animate-fade-in-up">
                    <CardContent className="p-8">
                        <h2 className="text-xl font-bold mb-1">Entrar no sistema</h2>
                        <p className="text-sm text-muted-foreground mb-6">Informe seu email e senha</p>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="seu@email.com"
                                    autoComplete="email"
                                    data-testid="login-email"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="password">Senha</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    data-testid="login-password"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full gap-2"
                                disabled={loading}
                                data-testid="login-submit"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                                Entrar
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-muted-foreground mt-6">
                    Cooperativa Mista da Flona do Tapajós — CNPJ: 07.315.165/0001-00
                </p>
            </div>
        </div>
    );
}
